<template>
  <div class="collection-page">
    <CollectionGate :collection-handle="collectionHandle" />
    <CollectionHeader @typeChange="handleTypeChange" />
    <CollectionToolbar />
    <ProductGrid
      :products="productsWithSkeleton"
      :featured-slots="featuredSlots"
    />
    <Pagination v-if="showPagination" @loadMore="handleLoadMore" />
    <BackToTop />
    <Filters @filterChange="handleFilterChange" />
    <Modal
      v-model:open="showQuiz"
      :images="{
        smallMedia: 'https://placehold.co/390x390/',
        mediumMedia: 'https://placehold.co/390x390/',
        largeMedia: 'https://placehold.co/507x680/',
      }"
    >
      <Quiz
        :steps="[
          {
            component: 'sign-up-forms/v2/Step1.vue',
          },
          {
            component: 'sign-up-forms/v2/Step2.vue',
          },
          {
            component: 'sign-up-forms/v2/Step3.vue',
          },
          {
            component: 'sign-up-forms/v2/Step4.vue',
          },
        ]"
        @close="showQuiz = false"
      >
        <template #header>
          <h2>Would you like to join the Rat &amp; Boa World?</h2>
        </template>
      </Quiz>
    </Modal>
  </div>
</template>

<script>
import CollectionHeader from "@/components/collection/CollectionHeader.vue";
import CollectionToolbar from "@/components/collection/CollectionToolbar.vue";
import ProductGrid from "@/components/collection/ProductGridWithFeatured.vue";
import Pagination from "@/components/collection/Pagination.vue";
import BackToTop from "@/components/BackToTop.vue";
import Filters from "@/components/collection/FiltersV2.vue";
import CollectionGate from "@/components/collection/CollectionGate.vue";

import collectionQuery from "@/graphql/queries/CollectionV2.gql";
import { PER_PAGE } from "@/helpers/pagination";
import { mapGetters, mapState, mapActions } from "vuex";
import isEmpty from "lodash-es/isEmpty";
import { viewCollectionEvent } from "@/helpers/events.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import Quiz from "@/components/Quiz.vue";
import Modal from "@/components/Modal.vue";

export default {
  name: "MidGridFeaturedCollection",
  components: {
    CollectionHeader,
    CollectionToolbar,
    ProductGrid,
    Pagination,
    BackToTop,
    Filters,
    Quiz,
    Modal,
    CollectionGate,
  },
  props: {
    collectionHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      lastPaginationCursor: null,
      quizCompleted: false,
      showQuiz: false,
    };
  },
  computed: {
    ...mapState("collection", ["collection", "loading", "filters", "sort"]),
    ...mapGetters("collection", ["products"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapState("auth", ["customer"]),
    showPagination() {
      return this.collection?.products?.pageInfo?.hasNextPage;
    },
    showQuizCTA() {
      const { enableQuizCTA = true } = getFeatureToggles();
      const hasAnsweredQuestionnaire =
        localStorage.getItem("hasAnsweredQuestionnaire") === "true";

      if (this.quizCompleted) {
        return false;
      }
      if (enableQuizCTA) {
        if (this.isLoggedIn) {
          return !this.customer.questionnaire;
        } else {
          return !hasAnsweredQuestionnaire;
        }
      }
      return false;
    },
    skeletonProducts() {
      return new Array(PER_PAGE).fill({ skeleton: true });
    },
    productsWithSkeleton() {
      const products = [...this.products];
      if (this.loading) {
        return [...products, ...this.skeletonProducts];
      }
      return products;
    },
    featuredSlots() {
      const featured =
        this.collection?.featuredProductCards.references.edges.map(
          ({ node }) => node
        ) || [];

      return featured;
    },
  },
  watch: {
    sort() {
      this.handleSortChange();
    },
  },
  methods: {
    ...mapActions("collection", [
      "setCollection",
      "setLoading",
      "setFilters",
      "setPriceRange",
    ]),
    handleSortChange() {
      const { value, reverse } = this.sort;

      this.$apollo.queries.collection.refetch({
        sort: value,
        reverse,
      });
    },
    async handleLoadMore() {
      if (!this.collection.products.pageInfo.hasNextPage) {
        return;
      }

      if (this.loading) {
        return;
      }

      this.setLoading(true);

      const edges = this.collection.products?.edges;
      const lastEdge = edges[edges.length - 1];
      const paginationCursor = lastEdge.cursor;

      if (this.lastPaginationCursor === paginationCursor) {
        console.warn("Duplicate pagination cursor");
      }

      try {
        await this.$apollo.queries.collection.fetchMore({
          variables: {
            after: paginationCursor,
          },
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
        this.lastPaginationCursor = paginationCursor;
      }
    },
    handleTypeChange(filters) {
      const keysToFilter = ["productType", "productMetafield"];

      const filtersWithoutTypes = this.filters.filter(
        (filter) => !keysToFilter.includes(Object.keys(filter)[0])
      );

      if (Object.values(filters)[0]) {
        filtersWithoutTypes.push(filters);
      }

      this.setFilters(filtersWithoutTypes);
      this.$apollo.queries.collection.refetch({
        product_filters: filtersWithoutTypes,
      });
    },
    handleFilterChange(filters) {
      this.setFilters(filters);

      this.$apollo.queries.collection.refetch({
        product_filters: filters,
      });
    },
  },
  apollo: {
    collection: {
      query: collectionQuery,
      skip() {
        return !this.collectionHandle;
      },
      variables() {
        return {
          collection: this.collectionHandle,
          first: PER_PAGE,
          sort: "MANUAL",
        };
      },
      result(resp) {
        const data = resp.data;

        this.setCollection(data?.collection);

        viewCollectionEvent(data?.collection.title);

        if (isEmpty(this.filters)) {
          const filters = data?.collection.products.filters;
          if (filters) {
            const priceFilter = filters.find(
              (filter) => filter.id === "filter.v.price"
            );

            if (priceFilter) {
              const { price } = JSON.parse(priceFilter.values[0].input);
              this.setPriceRange(price);
            }
          }
        }

        this.setLoading(false);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.collection-page {
  position: relative;
  /* No fixed height needed for full-screen overlay */
}
</style>
